import { createContext, useContext, useState } from "react";

export const translations = {
  en: {
    nav: { home: "Home", biography: "Biography", vj8c: "Otto Cilindri Manuale", biventi: "Bi Venti", enquire: "Enquire" },
    hero: {
      eyebrow: "Torino · MMXXV",
      lines: ["When mechanics", "becomes art"],
      sub: "Jano Automobili proposes automobiles in the spirit of Vittorio Jano, the engineer who taught engines to sing.",
      scroll: "Scroll",
    },
    manifesto: {
      eyebrow: "The Atelier",
      chapters: [
        { n: "01", title: "Vision", text: "A world where the automobile is once again a work of mechanical art, built by hand, driven by emotion, engineered without compromise." },
        { n: "02", title: "Mission", text: "To honour the legacy of Vittorio Jano with a small series of machines that carry his DNA: great engines, pure lines, the courage to innovate." },
      ],
    },
    jano: {
      eyebrow: "The Maestro",
      title: "Vittorio Jano",
      quote: "“The engine is the soul of the automobile. Everything else is a consequence.”",
      text: "From the Alfa Romeo P2 to the Lancia D50, one mind shaped the golden age of racing. Jano Automobili exists to continue that thought.",
      cta: "Read the biography",
      cap1: "Vittorio Jano with Alberto Ascari, Luigi Villoresi, and Eugenio Castellotti. Original picture from unknown author, appearing at the page 37, number 10 of 10 April 1955 of the magazine “Auto Italian”",
      cap2: "With the Grand Prix machine, 1920s",
    },
    marquee: ["Performance", "Heritage", "Precision", "Craftsmanship", "Torino"],
    teaser: {
      eyebrow: "The First Machine",
      title: "Otto Cilindri Manuale",
      text: "A classic sport tourer built on demand over your Ferrari 360 Modena: you give us the donor car, we hand back a rare, unique piece of fine engineering upon the Jano legacy.",
      cta: "Explore the car",
      stats: [
        { label: "Engine", value: "3.6 L V8" },
        { label: "Power", value: "425 CV" },
        { label: "Production", value: "On Demand" },
      ],
    },
    teaser2: {
      eyebrow: "The Second Machine",
      title: "Bi Venti",
      text: "An elegant coupé inspired by the Lancia Aurelia B20, built on the solid Alfa Romeo GT layout and powered by the legendary 3.2-litre V6 “Busso”, an engine that gifts a unique dynamic character and never fails to amaze the driver.",
      cta: "Explore the car",
      stats: [
        { label: "Engine", value: "3.2 L V6" },
        { label: "Power", value: "250 HP" },
        { label: "Production", value: "On Demand" },
      ],
    },
    contact: {
      eyebrow: "Enquiries",
      title: "Begin the conversation.",
      text: "Allocations, press and partnerships: write to us. Every message is read by a human being in Torino.",
      cta: "Enquire now",
    },
    form: {
      title: "Enquiry",
      name: "Name",
      email: "Email",
      interest: "Interest",
      interests: ["General enquiry", "Otto Cilindri Manuale commission", "Press", "Partnership"],
      message: "Message",
      submit: "Send enquiry",
      sending: "Sending…",
      success: "Enquiry sent. We will reply shortly.",
      error: "Something went wrong. Please try again.",
      validation: "Please write a message of at least 10 characters.",
    },
    footer: {
      tagline: "Automobili Artigianali · Torino",
      rights: "© 2026 Jano Automobili. All Rights Reserved.",
      disclaimer: "Jano Automobili is not affiliated with, sponsored or endorsed by Ferrari S.p.A. or Stellantis N.V. Ferrari® and 360 Modena® are registered trademarks of Ferrari S.p.A.; Alfa Romeo®, Lancia® and Aurelia® are registered trademarks of Stellantis N.V. All trademarks are the property of their respective owners.",
    },
    bio: {
      eyebrow: "The Maestro · 1891 - 1966",
      title: "Vittorio Jano",
      intro: "Vittorio Jano was born Viktor János in 1891 in San Giorgio Canavese, in the hills above Turin, the son of Hungarian parents who had settled in Piedmont. He never sought the spotlight. Yet for four decades the fastest cars on earth were powered by his ideas.",
      intro2: "Entirely self-taught and without an engineering degree, he began as a draftsman at Fiat in 1911 and was chief designer by thirty, his Grand Prix cars winning from the very start. In 1923 Alfa Romeo called, on the advice of a young racing driver named Enzo Ferrari, and Jano answered with the P2: victorious on its debut in 1924, World Champion in 1925.",
      intro3: "The P3 followed, and the 8C that ruled Le Mans. In 1937 he moved to Lancia, where he created the Aurelia, the first production car with a V6 engine, and the D50, the Formula 1 machine that carried Juan Manuel Fangio to the 1956 World Championship in Ferrari red. This is the story of the man whose name we carry.",
      timeline: [
        { year: "1891", title: "San Giorgio Canavese", text: "Vittorio Jano is born on 22 April 1891 near Turin, christened Viktor János after his Hungarian father, a technical draftsman. He learns to draw before he learns to doubt." },
        { year: "1911", title: "FIAT", text: "At twenty he joins FIAT as a draughtsman and cuts his teeth on the 2-litre 805 Grand Prix car. By 1921 he is head of the design office." },
        { year: "1923", title: "Alfa Romeo", text: "Enzo Ferrari and Giorgio Rimini convince Jano to move to Milan. His first creation, the P2, wins the 1925 Grand Prix World Championship." },
        { year: "1931", title: "The 8C", text: "The 8C 2300, a straight-eight of genius built as two four-cylinder blocks on a single crankcase, conquers Le Mans four years in succession. Then comes the P3 Monoposto, winner of the Italian Grand Prix." },
        { year: "1937", title: "Lancia", text: "At Lancia, Jano designs the Aurelia with the first successful production V6 in history, and the revolutionary D50 Grand Prix car with its fuel carried between the wheels." },
        { year: "1955", title: "Ferrari", text: "When Lancia withdraws from racing, Jano joins Ferrari full-time and fathers the Dino V6 and V8 engines, the beating heart of Maranello for a generation." },
        { year: "1966", title: "The Legacy", text: "Vittorio Jano dies in Turin in 1966, aged 75. His method: lightness, balance, mechanical courage. It becomes our founding charter. Jano Automobili begins where he left off." },
      ],
      quote: "“I have never designed an engine to be looked at. But I have never accepted one that was not beautiful.”",
      disclaimer: {
        button: "Disclaimer",
        title: "Disclaimer",
        p1: "Vittorio Jano was Alfa Romeo's chief engineer through the marque's defining competition years, responsible for the P2 and the P3, the cars that won Alfa Romeo its first World Championship. In 1937 he moved to Lancia, where he designed the Aurelia, the car that more than any other defined what a modern Italian grand tourer could be. It is that lineage, elegance built on engineering discipline rather than excess, that this project takes as its reference point.",
        p2: "Jano Automobili exists to keep that spirit in view, not to claim it. This is an independent tribute, built with no family, corporate, or legal affiliation to Vittorio Jano, his estate, Alfa Romeo, or Stellantis N.V. The name is used to honour an engineer whose work still defines what a great Italian GT should feel like. Nothing more is implied, and nothing more should be inferred.",
      },
      capMain: "Vittorio Jano, the Maranello years",
      capSuit: "Torino, the early years",
      capSeated: "At the wheel, 1950s",
    },
    vj8c: {
      eyebrow: "The First Machine · Built on Demand",
      titleLines: ["Otto Cilindri", "Manuale"],
      heroSub: "A naturally aspirated V8. Built on demand, on your Ferrari 360 Modena.",
      detailsCap: "The Tail",
      chapters: [
        { n: "01", title: "The Philosophy", text: "No screens. No filters. A machine reduced to its essence: engine, chassis, driver. The Otto Cilindri Manuale is our answer to an age of digital noise: an analogue manifesto on four wheels.", img: "/images/ocm2-front.jpg" },
        { n: "02", title: "The Engine", text: "A 3.6-litre naturally aspirated V8, as Jano would have demanded: 40 valves, 425 CV at 8,500 rpm, and a voice that needs no loudspeakers.", img: "/images/ocm2-rear.jpg" },
        { n: "03", title: "The Body", text: "Every new body panel and mechanical component is executed in high-quality carbon fibre and advanced composites: lightness, resilience and durability, engineered without compromise.", img: "/images/ocm2-side.jpg" },
      ],
      specsLabel: "Specification",
      specs: [
        { label: "Engine", value: "3.6 L V8 · 40 Valves" },
        { label: "Power", value: "425 CV @ 8,500 rpm" },
        { label: "Weight", value: "1,350 kg dry" },
        { label: "Acceleration", value: "0-100 km/h · 4.0 s" },
        { label: "Top Speed", value: "305 km/h" },
        { label: "Production", value: "On Demand" },
      ],
      gallery: [
        { src: "/images/ocm3-1.jpg", caption: "The Front" },
        { src: "/images/ocm3-2.jpg", caption: "The Tail" },
        { src: "/images/ocm3-8.jpg", caption: "In White" },
        { src: "/images/ocm3-3.jpg", caption: "In Yellow" },
        { src: "/images/ocm3-9.jpg", caption: "In Red" },
        { src: "/images/ocm3-4.jpg", caption: "In Red" },
        { src: "/images/ocm3-5b.jpg", caption: "In Orange" },
        { src: "/images/ocm3-6.jpg", caption: "Blue, from Behind" },
        { src: "/images/ocm3-10.jpg", caption: "Head-On" },
        { src: "/images/ocm3-7b.jpg", caption: "In Blue" },
      ],
      cta: {
        title: "Your 360 Modena, reborn.",
        text: "Each Otto Cilindri Manuale is built on demand for Ferrari 360 Modena owners who wish to use their car as a donor and receive back a rare, unique piece of fine engineering upon the Jano legacy.",
        button: "Commission yours",
      },
    },
    biventi: {
      eyebrow: "The Second Machine · Built on Demand",
      titleLines: ["Bi Venti"],
      heroSub: "An elegant V6 coupé, built on demand in the spirit of the Aurelia.",
      chapters: [
        { n: "01", title: "The Philosophy", text: "Where the Otto Cilindri Manuale is pure speed, the Bi Venti is the grand tourer: an elegant coupé in the spirit of the Lancia Aurelia B20, engineering discipline dressed in grace, built for distance rather than lap times." },
        { n: "02", title: "The Engine", text: "The legendary 3.2-litre V6 “Busso”, among the greatest Italian engines ever made: 250 HP on the solid Alfa Romeo GT layout, with a voice that needs no loudspeakers." },
        { n: "03", title: "The Body", text: "Every new body panel and mechanical component is executed in high-quality carbon fibre and advanced composites: lightness, resilience and durability, engineered without compromise." },
      ],
      specsLabel: "Specification",
      specs: [
        { label: "Engine", value: "3.2 L V6" },
        { label: "Power", value: "250 HP" },
        { label: "Production", value: "On Demand" },
      ],
      gallery: [
        { src: "/images/bv3-1.jpg", caption: "The Duo" },
        { src: "/images/bv2-2.jpg", caption: "Front Three-Quarters" },
        { src: "/images/bv2-3.jpg", caption: "The Front" },
        { src: "/images/bv-5.jpg", caption: "The Tail" },
        { src: "/images/bv-7.jpg", caption: "From Above" },
        { src: "/images/bv-8.jpg", caption: "Low Angle" },
      ],
      cta: {
        title: "Your Bi Venti, built for you.",
        text: "Each Bi Venti is built on demand: a rare, unique piece of fine engineering upon the Jano legacy.",
        button: "Commission yours",
      },
    },
  },
  it: {
    nav: { home: "Home", biography: "Biografia", vj8c: "Otto Cilindri Manuale", biventi: "Bi Venti", enquire: "Richiedi" },
    hero: {
      eyebrow: "Torino · MMXXV",
      lines: ["Quando la meccanica", "diventa arte"],
      sub: "Jano Automobili propone automobili nello spirito di Vittorio Jano, l’ingegnere che insegnò ai motori a cantare.",
      scroll: "Scorri",
    },
    manifesto: {
      eyebrow: "L’Atelier",
      chapters: [
        { n: "01", title: "Visione", text: "Un mondo in cui l’automobile torna a essere un’opera d’arte meccanica, costruita a mano, guidata dall’emozione, progettata senza compromessi." },
        { n: "02", title: "Missione", text: "Onorare l’eredità di Vittorio Jano con una piccola serie di macchine che ne portano il DNA: i grandi motori, la purezza della linea, il coraggio di innovare." },
      ],
    },
    jano: {
      eyebrow: "Il Maestro",
      title: "Vittorio Jano",
      quote: "«Il motore è l’anima dell’automobile. Tutto il resto è una conseguenza.»",
      text: "Dall’Alfa Romeo P2 alla Lancia D50, una sola mente ha plasmato l’epoca d’oro delle corse. Jano Automobili esiste per continuare quel pensiero.",
      cta: "Leggi la biografia",
      cap1: "Vittorio Jano con Alberto Ascari, Luigi Villoresi ed Eugenio Castellotti. Foto originale di autore sconosciuto, apparsa a pagina 37, numero 10 del 10 aprile 1955 della rivista “Auto Italian”",
      cap2: "Con la macchina da Gran Premio, anni ’20",
    },
    marquee: ["Prestazione", "Eredità", "Precisione", "Artigianalità", "Torino"],
    teaser: {
      eyebrow: "La Prima Macchina",
      title: "Otto Cilindri Manuale",
      text: "Una classica sport tourer, costruita su richiesta sulla vostra Ferrari 360 Modena: ci consegnate la vostra auto, vi restituiamo un pezzo raro e unico di alta ingegneria, nell’eredità di Jano.",
      cta: "Esplora la macchina",
      stats: [
        { label: "Motore", value: "3,6 L V8" },
        { label: "Potenza", value: "425 CV" },
        { label: "Produzione", value: "Su Richiesta" },
      ],
    },
    teaser2: {
      eyebrow: "La Seconda Macchina",
      title: "Bi Venti",
      text: "Un’elegante coupé ispirata alla Lancia Aurelia B20, costruita sulla solida base dell’Alfa Romeo GT e spinta dal leggendario V6 3,2 litri “Busso”, un motore capace di regalare un carattere dinamico unico e di stupire sempre chi guida.",
      cta: "Esplora la macchina",
      stats: [
        { label: "Motore", value: "3,2 L V6" },
        { label: "Potenza", value: "250 CV" },
        { label: "Produzione", value: "Su Richiesta" },
      ],
    },
    contact: {
      eyebrow: "Richieste",
      title: "Iniziamo la conversazione.",
      text: "Assegnazioni, stampa e partnership: scriveteci. Ogni messaggio viene letto da una persona, a Torino.",
      cta: "Richiedi ora",
    },
    form: {
      title: "Richiesta",
      name: "Nome",
      email: "Email",
      interest: "Interesse",
      interests: ["Richiesta generale", "Commissione Otto Cilindri Manuale", "Stampa", "Partnership"],
      message: "Messaggio",
      submit: "Invia richiesta",
      sending: "Invio in corso…",
      success: "Richiesta inviata. Vi risponderemo al più presto.",
      error: "Qualcosa è andato storto. Riprova.",
      validation: "Scrivi un messaggio di almeno 10 caratteri.",
    },
    footer: {
      tagline: "Automobili Artigianali · Torino",
      rights: "© 2026 Jano Automobili. Tutti I Diritti Riservati.",
      disclaimer: "Jano Automobili non è affiliato, sponsorizzato o autorizzato da Ferrari S.p.A. o Stellantis N.V. Ferrari® e 360 Modena® sono marchi registrati di Ferrari S.p.A.; Alfa Romeo®, Lancia® e Aurelia® sono marchi registrati di Stellantis N.V. Tutti i marchi appartengono ai rispettivi proprietari.",
    },
    bio: {
      eyebrow: "Il Maestro · 1891 - 1966",
      title: "Vittorio Jano",
      intro: "Vittorio Jano nacque Viktor János nel 1891 a San Giorgio Canavese, sulle colline sopra Torino, figlio di genitori ungheresi stabilitisi in Piemonte. Non cercò mai i riflettori. Eppure per quarant’anni le automobili più veloci del mondo furono mosse dalle sue idee.",
      intro2: "Completamente autodidatta e senza una laurea in ingegneria, iniziò come disegnatore alla Fiat nel 1911 e fu capo progettista a trent’anni, con le sue auto da Gran Premio vincenti fin da subito. Nel 1923 l’Alfa Romeo chiamò, su consiglio di un giovane pilota di nome Enzo Ferrari, e Jano rispose con la P2: vittoriosa al debutto nel 1924, Campione del Mondo nel 1925.",
      intro3: "Seguirono la P3 e la 8C che dominò Le Mans. Nel 1937 passò alla Lancia, dove creò l’Aurelia, la prima auto di serie con motore V6, e la D50, la monoposto di Formula 1 che portò Juan Manuel Fangio al titolo mondiale del 1956 vestita di rosso Ferrari. Questa è la storia dell’uomo di cui portiamo il nome.",
      timeline: [
        { year: "1891", title: "San Giorgio Canavese", text: "Vittorio Jano nasce il 22 aprile 1891 alle porte di Torino, battezzato Viktor János dal padre ungherese, disegnatore tecnico. Impara a disegnare prima ancora di imparare a dubitare." },
        { year: "1911", title: "FIAT", text: "A vent’anni entra in FIAT come disegnatore e si forma sulla 2 litri 805 da Gran Premio. Nel 1921 è a capo dell’ufficio progettazione." },
        { year: "1923", title: "Alfa Romeo", text: "Enzo Ferrari e Giorgio Rimini convincono Jano a trasferirsi a Milano. La sua prima creatura, la P2, vince il Campionato del Mondo di Gran Premi del 1925." },
        { year: "1931", title: "La 8C", text: "La 8C 2300, un otto cilindri in linea geniale costruito come due blocchi da quattro su un unico basamento, conquista Le Mans per quattro anni consecutivi. Poi arriva la P3 Monoposto, vincitrice del Gran Premio d’Italia." },
        { year: "1937", title: "Lancia", text: "Alla Lancia, Jano disegna l’Aurelia con il primo V6 di serie della storia, e la rivoluzionaria D50 da Gran Premio con il carburante fra le ruote." },
        { year: "1955", title: "Ferrari", text: "Quando la Lancia si ritira dalle corse, Jano entra in Ferrari a tempo pieno e dà vita ai motori Dino V6 e V8, il cuore pulsante di Maranello per una generazione." },
        { year: "1966", title: "L’Eredità", text: "Vittorio Jano muore a Torino nel 1966, a 75 anni. Il suo metodo: leggerezza, equilibrio, coraggio meccanico. Diventa il nostro statuto fondativo. Jano Automobili comincia dove lui si fermò." },
      ],
      quote: "«Non ho mai disegnato un motore per essere guardato. Ma non ne ho mai accettato uno che non fosse bello.»",
      disclaimer: {
        button: "Disclaimer",
        title: "Disclaimer",
        p1: "Vittorio Jano fu capo progettista dell'Alfa Romeo negli anni che definirono le competizioni del marchio, responsabile della P2 e della P3, le vetture che regalarono all'Alfa Romeo il suo primo Campionato del Mondo. Nel 1937 passò alla Lancia, dove disegnò l'Aurelia, l'auto che più di ogni altra definì ciò che una moderna gran turismo italiana poteva essere. È quella discendenza, un'eleganza costruita sulla disciplina ingegneristica piuttosto che sull'eccesso, che questo progetto prende come punto di riferimento.",
        p2: "Jano Automobili esiste per tenere vivo quello spirito, non per rivendicarlo. Questo è un tributo indipendente, realizzato senza alcuna affiliazione familiare, societaria o legale con Vittorio Jano, i suoi eredi, Alfa Romeo o Stellantis N.V. Il nome è usato per onorare un ingegnere il cui lavoro definisce ancora oggi ciò che una grande GT italiana dovrebbe saper trasmettere. Nient'altro è sottinteso, e nient'altro dovrebbe essere dedotto.",
      },
      capMain: "Vittorio Jano, gli anni di Maranello",
      capSuit: "Torino, i primi anni",
      capSeated: "Al volante, anni ’50",
    },
    vj8c: {
      eyebrow: "La Prima Macchina · Su Richiesta",
      titleLines: ["Otto Cilindri", "Manuale"],
      heroSub: "Un V8 aspirato. Costruita su richiesta, sulla vostra Ferrari 360 Modena.",
      detailsCap: "La Coda",
      chapters: [
        { n: "01", title: "La Filosofia", text: "Niente schermi. Niente filtri. Una macchina ridotta all’essenziale: motore, telaio, pilota. L’Otto Cilindri Manuale è la nostra risposta a un’epoca di rumore digitale: un manifesto analogico su quattro ruote.", img: "/images/ocm2-front.jpg" },
        { n: "02", title: "Il Motore", text: "Un V8 aspirato di 3,6 litri, come Jano avrebbe preteso: 40 valvole, 425 CV a 8.500 giri e una voce che non ha bisogno di altoparlanti.", img: "/images/ocm2-rear.jpg" },
        { n: "03", title: "La Carrozzeria", text: "Ogni nuovo componente della carrozzeria e degli organi meccanici è realizzato in fibra di carbonio di alta qualità e compositi avanzati: leggerezza, resistenza e durata, senza compromessi.", img: "/images/ocm2-side.jpg" },
      ],
      specsLabel: "Scheda Tecnica",
      specs: [
        { label: "Motore", value: "3,6 L V8 · 40 Valvole" },
        { label: "Potenza", value: "425 CV @ 8.500 giri" },
        { label: "Peso", value: "1.350 kg a secco" },
        { label: "Accelerazione", value: "0-100 km/h · 4,0 s" },
        { label: "Velocità max", value: "305 km/h" },
        { label: "Produzione", value: "Su Richiesta" },
      ],
      gallery: [
        { src: "/images/ocm3-1.jpg", caption: "Il Frontale" },
        { src: "/images/ocm3-2.jpg", caption: "La Coda" },
        { src: "/images/ocm3-8.jpg", caption: "In Bianco" },
        { src: "/images/ocm3-3.jpg", caption: "In Giallo" },
        { src: "/images/ocm3-9.jpg", caption: "In Rosso" },
        { src: "/images/ocm3-4.jpg", caption: "In Rosso" },
        { src: "/images/ocm3-5b.jpg", caption: "In Arancione" },
        { src: "/images/ocm3-6.jpg", caption: "Blu, da Dietro" },
        { src: "/images/ocm3-10.jpg", caption: "Di Fronte" },
        { src: "/images/ocm3-7b.jpg", caption: "In Blu" },
      ],
      cta: {
        title: "La vostra 360 Modena, rinata.",
        text: "Ogni Otto Cilindri Manuale nasce su richiesta per i proprietari di Ferrari 360 Modena che desiderano usare la loro auto come donatrice e ricevere indietro un pezzo raro e unico di alta ingegneria, nell’eredità di Jano.",
        button: "Commissiona la tua",
      },
    },
    biventi: {
      eyebrow: "La Seconda Macchina · Costruita su Richiesta",
      titleLines: ["Bi Venti"],
      heroSub: "Un’elegante coupé V6, costruita su richiesta nello spirito dell’Aurelia.",
      chapters: [
        { n: "01", title: "La Filosofia", text: "Dove l’Otto Cilindri Manuale è pura velocità, la Bi Venti è la gran turismo: una coupé elegante nello spirito della Lancia Aurelia B20, disciplina ingegneristica vestita di grazia, fatta per la distanza più che per il giro veloce." },
        { n: "02", title: "Il Motore", text: "Il leggendario V6 3,2 litri “Busso”, tra i più grandi motori italiani di sempre: 250 CV sulla solida base dell’Alfa Romeo GT, con una voce che non ha bisogno di altoparlanti." },
        { n: "03", title: "La Carrozzeria", text: "Ogni nuovo pannello e componente meccanico è realizzato in fibra di carbonio di alta qualità e materiali compositi avanzati: leggerezza, resistenza e durata, senza compromessi." },
      ],
      specsLabel: "Specifiche",
      specs: [
        { label: "Motore", value: "3,2 L V6" },
        { label: "Potenza", value: "250 CV" },
        { label: "Produzione", value: "Su Richiesta" },
      ],
      gallery: [
        { src: "/images/bv3-1.jpg", caption: "La Coppia" },
        { src: "/images/bv2-2.jpg", caption: "Tre Quarti Anteriore" },
        { src: "/images/bv2-3.jpg", caption: "Il Frontale" },
        { src: "/images/bv-5.jpg", caption: "La Coda" },
        { src: "/images/bv-7.jpg", caption: "Dall'Alto" },
        { src: "/images/bv-8.jpg", caption: "Dal Basso" },
      ],
      cta: {
        title: "La vostra Bi Venti, costruita per voi.",
        text: "Ogni Bi Venti nasce su richiesta: un pezzo raro e unico di alta ingegneria, nell’eredità di Jano.",
        button: "Commissiona la tua",
      },
    },
  },
};

const LanguageContext = createContext(null);

export const useLang = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("jano-lang") || "en");
  const change = (l) => {
    localStorage.setItem("jano-lang", l);
    setLang(l);
  };
  return (
    <LanguageContext.Provider value={{ lang, setLang: change, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};
